
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2>{`Theming`}</h2>
    <p>{`styled-components has full theming support by exporting a `}<inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` wrapper component.
This component provides a theme to all React components underneath itself via the context API. In the render
tree all styled-components will have access to the provided theme, even when they are multiple levels deep.`}</p>
    <p>{`To illustrate this, let's create our Button component, but this time we'll pass some variables down
as a theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`// Define our button, but with the use of props.theme this time
const Button = styled.button\`
  font-size: 1em;
  margin: 1em;
  padding: 0.25em 1em;
  border-radius: 3px;

  /* Color the border and text with theme.main */
  color: \${props => props.theme.main};
  border: 2px solid \${props => props.theme.main};
\`;

// We are passing a default theme for Buttons that arent wrapped in the ThemeProvider
Button.defaultProps = {
  theme: {
    main: "palevioletred"
  }
}

// Define what props.theme will look like
const theme = {
  main: "mediumseagreen"
};

render(
  <div>
    <Button>Normal</Button>

    <ThemeProvider theme={theme}>
      <Button>Themed</Button>
    </ThemeProvider>
  </div>
);
`}</code></pre>
    <h3>{`Function themes`}</h3>
    <p>{`You can also pass a function for the theme prop. This function will receive the parent theme, that is from
another `}<inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` higher up the tree. This way themes themselves can be made contextual.`}</p>
    <p>{`This example renders our above themed Button and a second one that uses a second ThemeProvider to invert the
background and foreground colors. The function `}<inlineCode parentName="p">{`invertTheme`}</inlineCode>{` receives the upper theme and creates a new one.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`// Define our button, but with the use of props.theme this time
const Button = styled.button\`
  color: \${props => props.theme.fg};
  border: 2px solid \${props => props.theme.fg};
  background: \${props => props.theme.bg};

  font-size: 1em;
  margin: 1em;
  padding: 0.25em 1em;
  border-radius: 3px;
\`;

// Define our \`fg\` and \`bg\` on the theme
const theme = {
  fg: "palevioletred",
  bg: "white"
};

// This theme swaps \`fg\` and \`bg\`
const invertTheme = ({ fg, bg }) => ({
  fg: bg,
  bg: fg
});

render(
  <ThemeProvider theme={theme}>
    <div>
      <Button>Default Theme</Button>

      <ThemeProvider theme={invertTheme}>
        <Button>Inverted Theme</Button>
      </ThemeProvider>
    </div>
  </ThemeProvider>
);
`}</code></pre>
    <h3>{`Getting the theme without styled components`}</h3>
    <h4>{`via `}<inlineCode parentName="h4">{`withTheme`}</inlineCode>{` higher-order component`}</h4>
    <p>{`If you ever need to use the current theme outside styled components (e.g. inside big components), you can use
the `}<inlineCode parentName="p">{`withTheme`}</inlineCode>{` higher order component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { withTheme } from 'styled-components';

class MyComponent extends React.Component {
  render() {
    console.log('Current theme: ', this.props.theme);
    // ...
  }
}

export default withTheme(MyComponent);
`}</code></pre>
    <h4>{`via `}<inlineCode parentName="h4">{`useContext`}</inlineCode>{` React hook | v4`}</h4>
    <p>{`You can also use `}<inlineCode parentName="p">{`useContext`}</inlineCode>{` to access the current theme outside of styled components when working with React Hooks.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { useContext } from 'react';
import { ThemeContext } from 'styled-components';

const MyComponent = () => {
  const themeContext = useContext(ThemeContext);

  console.log('Current theme: ', themeContext);
  // ...
}
`}</code></pre>
    <h3>{`The `}<inlineCode parentName="h3">{`theme`}</inlineCode>{` prop`}</h3>
    <p>{`A theme can also be passed down to a component using the `}<inlineCode parentName="p">{`theme`}</inlineCode>{` prop.`}</p>
    <p>{`This is useful to circumvent a missing `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` or to override it.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`// Define our button
const Button = styled.button\`
  font-size: 1em;
  margin: 1em;
  padding: 0.25em 1em;
  border-radius: 3px;

  /* Color the border and text with theme.main */
  color: \${props => props.theme.main};
  border: 2px solid \${props => props.theme.main};
\`;

// Define what main theme will look like
const theme = {
  main: "mediumseagreen"
};

render(
  <div>
    <Button theme={{ main: "royalblue" }}>Ad hoc theme</Button>
    <ThemeProvider theme={theme}>
      <div>
        <Button>Themed</Button>
        <Button theme={{ main: "darkorange" }}>Overridden</Button>
      </div>
    </ThemeProvider>
  </div>
);
`}</code></pre>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;